// Shelf Support System Calculator
// Calculates optimal support configuration using PMMA rods and tubes

import { MaterialProperties } from './MaterialDatabase';

export interface ShelfSystemInput {
  // Wymiary półki
  shelfWidth: number;      // mm
  shelfDepth: number;      // mm
  shelfThickness: number;  // mm
  shelfMaterial: string;   // materiał półki
  
  // Obciążenie
  uniformLoad: number;     // kg/m²
  pointLoads?: {          // opcjonalne obciążenia punktowe
    x: number;
    y: number;
    weight: number;        // kg
  }[];
  
  // Parametry systemu
  maxShelfDeflection?: number;  // mm (domyślnie L/200)
  supportType: 'rod' | 'tube' | 'both';  // typ podpór do rozważenia
  maxSupportSpacing?: number;     // maksymalny rozstaw podpór (mm)
  minSupportSpacing?: number;     // minimalny rozstaw podpór (mm)
  supportHeight: number;          // wysokość słupków (mm)
  numberOfShelves?: number;       // liczba półek w systemie
  shelfSpacing?: number;          // rozstaw między półkami (mm)
  
  // Ograniczenia
  maxSupportDiameter?: number;    // maksymalna średnica słupka (mm)
  preferredSupportCount?: number; // preferowana liczba podpór
  cornerSupportsRequired?: boolean; // czy wymagane podpory w narożach
  edgeSupportsRequired?: boolean;   // czy wymagane podpory na krawędziach
  
  // Współczynniki bezpieczeństwa
  safetyFactor: number;           // współczynnik bezpieczeństwa (domyślnie 3)
  bucklingFactor?: number;        // współczynnik wyboczenia (domyślnie 2)
}

export interface SupportConfiguration {
  type: 'rod' | 'tube';
  diameter: number;           // średnica zewnętrzna (mm)
  innerDiameter?: number;     // średnica wewnętrzna dla rurki (mm)
  wallThickness?: number;     // grubość ścianki dla rurki (mm)
  count: number;              // liczba słupków
  positions: {                // pozycje słupków
    x: number;
    y: number;
  }[];
  material: string;           // materiał słupków (PMMA)
  
  // Wyniki obliczeń
  maxStressInSupport: number; // maksymalne naprężenie w słupku (MPa)
  maxBuckling: number;        // współczynnik wyboczenia
  maxShelfDeflection: number; // maksymalne ugięcie półki (mm)
  totalWeight: number;        // całkowita waga słupków (kg)
  totalCost: number;          // szacunkowy koszt słupków (PLN)
  
  // Bezpieczeństwo
  isSafe: boolean;
  safetyMargin: number;       // % marginesu bezpieczeństwa
  criticalMode: 'stress' | 'buckling' | 'deflection' | 'none';
  
  // Rekomendacje
  recommendations: string[];
  warnings?: string[];
}

export interface ShelfSystemResult {
  optimalConfigurations: SupportConfiguration[];
  shelfAnalysis: {
    totalLoad: number;         // całkowite obciążenie (kg)
    loadPerSupport: number[];  // obciążenie na każdy słupek (kg)
    maxDeflection: number;     // maksymalne ugięcie półki (mm)
    deflectionMap?: number[][]; // mapa ugięć
    stressMap?: number[][];    // mapa naprężeń
  };
  recommendations: string[];
  installationGuide?: string[];
}

export class ShelfSupportCalculator {
  private material: MaterialProperties;
  
  constructor(material: MaterialProperties) {
    this.material = material;
  }
  
  /**
   * Oblicz optymalną konfigurację podpór dla systemu półkowego
   */
  public calculate(input: ShelfSystemInput): ShelfSystemResult {
    const configurations: SupportConfiguration[] = [];
    
    // 1. Oblicz całkowite obciążenie
    const totalLoad = this.calculateTotalLoad(input);
    
    // 2. Określ minimalną liczbę podpór
    const minSupports = this.calculateMinimumSupports(input, totalLoad);
    
    // 3. Generuj różne konfiguracje
    const supportCounts = this.generateSupportCounts(minSupports, input.preferredSupportCount);
    
    for (const count of supportCounts) {
      // Dla prętów
      if (input.supportType === 'rod' || input.supportType === 'both') {
        const rodConfig = this.calculateRodConfiguration(input, totalLoad, count);
        if (rodConfig && rodConfig.isSafe) {
          configurations.push(rodConfig);
        }
      }
      
      // Dla rurek
      if (input.supportType === 'tube' || input.supportType === 'both') {
        const tubeConfig = this.calculateTubeConfiguration(input, totalLoad, count);
        if (tubeConfig && tubeConfig.isSafe) {
          configurations.push(tubeConfig);
        }
      }
    }
    
    // 4. Sortuj konfiguracje według optymalności
    configurations.sort((a, b) => {
      // Priorytet: bezpieczeństwo > koszt > waga
      if (a.safetyMargin !== b.safetyMargin) {
        return b.safetyMargin - a.safetyMargin;
      }
      if (a.totalCost !== b.totalCost) {
        return a.totalCost - b.totalCost;
      }
      return a.totalWeight - b.totalWeight;
    });
    
    // 5. Analiza ugięcia półki
    const shelfAnalysis = this.analyzeShelfDeflection(
      input,
      configurations[0] || this.getDefaultConfiguration(input)
    );
    
    // 6. Generuj rekomendacje
    const recommendations = this.generateRecommendations(input, configurations, shelfAnalysis);
    
    // 7. Instrukcja montażu
    const installationGuide = this.generateInstallationGuide(input, configurations[0]);
    
    return {
      optimalConfigurations: configurations.slice(0, 5), // Top 5 konfiguracji
      shelfAnalysis,
      recommendations,
      installationGuide
    };
  }
  
  /**
   * Oblicz całkowite obciążenie systemu
   */
  private calculateTotalLoad(input: ShelfSystemInput): number {
    // Obciążenie równomierne
    const uniformLoadTotal = (input.shelfWidth * input.shelfDepth * input.uniformLoad) / 1000000;
    
    // Obciążenia punktowe
    const pointLoadTotal = input.pointLoads?.reduce((sum, load) => sum + load.weight, 0) || 0;
    
    // Waga samej półki
    const shelfWeight = this.calculateShelfWeight(input);
    
    // Jeśli system wielopoziomowy
    const numberOfShelves = input.numberOfShelves || 1;
    
    return (uniformLoadTotal + pointLoadTotal + shelfWeight) * numberOfShelves;
  }
  
  /**
   * Oblicz wagę półki
   */
  private calculateShelfWeight(input: ShelfSystemInput): number {
    // Zakładamy PMMA dla półki jeśli nie określono
    const density = 1190; // kg/m³ dla PMMA
    const volume = (input.shelfWidth * input.shelfDepth * input.shelfThickness) / 1000000000; // m³
    return volume * density;
  }
  
  /**
   * Określ minimalną liczbę podpór
   */
  private calculateMinimumSupports(input: ShelfSystemInput, totalLoad: number): number {
    // Podstawowe wymaganie: 4 narożniki
    let minSupports = 4;
    
    // Dodatkowe podpory dla dużych rozpiętości
    const maxSpacing = input.maxSupportSpacing || 800; // mm
    const supportedArea = input.shelfWidth * input.shelfDepth;
    const areaPerSupport = maxSpacing * maxSpacing;
    
    minSupports = Math.max(minSupports, Math.ceil(supportedArea / areaPerSupport));
    
    // Sprawdź obciążenie na pojedynczą podporę
    const maxLoadPerSupport = this.calculateMaxLoadPerSupport();
    minSupports = Math.max(minSupports, Math.ceil(totalLoad / maxLoadPerSupport));
    
    return minSupports;
  }
  
  /**
   * Oblicz maksymalne obciążenie na pojedynczą podporę
   */
  private calculateMaxLoadPerSupport(): number {
    // Dla PMMA pręta Ø20mm
    const maxDiameter = 20; // mm
    const area = Math.PI * (maxDiameter / 2) ** 2;
    const maxStress = this.material.compressiveStrength / 3; // z współczynnikiem bezpieczeństwa
    const maxForce = (area * maxStress) / 9.81; // kg
    return maxForce;
  }
  
  /**
   * Generuj różne liczby podpór do sprawdzenia
   */
  private generateSupportCounts(minSupports: number, preferred?: number): number[] {
    const counts: number[] = [];
    
    if (preferred && preferred >= minSupports) {
      counts.push(preferred);
    }
    
    // Standardowe konfiguracje
    [4, 6, 8, 9, 12, 16].forEach(count => {
      if (count >= minSupports && (!preferred || count !== preferred)) {
        counts.push(count);
      }
    });
    
    return counts;
  }
  
  /**
   * Oblicz konfigurację dla prętów
   */
  private calculateRodConfiguration(
    input: ShelfSystemInput,
    totalLoad: number,
    supportCount: number
  ): SupportConfiguration | null {
    // Standardowe średnice prętów PMMA
    const standardDiameters = [8, 10, 12, 15, 16, 20, 25, 30, 35, 40, 50];
    
    for (const diameter of standardDiameters) {
      if (input.maxSupportDiameter && diameter > input.maxSupportDiameter) {
        continue;
      }
      
      // Oblicz naprężenia
      const area = Math.PI * (diameter / 2) ** 2;
      const loadPerSupport = totalLoad / supportCount;
      const stress = (loadPerSupport * 9.81) / area; // MPa
      
      // Sprawdź wyboczenie (wzór Eulera)
      const momentOfInertia = Math.PI * diameter ** 4 / 64;
      const bucklingLoad = this.calculateBucklingLoad(
        momentOfInertia,
        input.supportHeight,
        'pinned-pinned'
      );
      const bucklingFactor = bucklingLoad / (loadPerSupport * 9.81);
      
      // Sprawdź bezpieczeństwo
      const allowableStress = this.material.compressiveStrength / input.safetyFactor;
      const isSafe = stress < allowableStress && bucklingFactor > (input.bucklingFactor || 2);
      
      if (isSafe) {
        // Rozmieść podpory
        const positions = this.calculateSupportPositions(input, supportCount);
        
        // Oblicz ugięcie półki
        const maxDeflection = this.estimateMaxDeflection(input, positions);
        
        // Oblicz wagę i koszt
        const volume = area * input.supportHeight * supportCount / 1000000; // m³
        const weight = volume * this.material.density;
        const cost = weight * (this.material.pricePerKg || 30);
        
        return {
          type: 'rod',
          diameter,
          count: supportCount,
          positions,
          material: 'PMMA',
          maxStressInSupport: stress,
          maxBuckling: bucklingFactor,
          maxShelfDeflection: maxDeflection,
          totalWeight: weight,
          totalCost: cost,
          isSafe: true,
          safetyMargin: ((1 - stress / allowableStress) * 100),
          criticalMode: stress / allowableStress > 0.8 ? 'stress' : 
                       bucklingFactor < 3 ? 'buckling' : 
                       maxDeflection > (input.maxShelfDeflection || input.shelfDepth / 200) ? 'deflection' : 
                       'none',
          recommendations: this.generateSupportRecommendations(diameter, supportCount, stress, bucklingFactor)
        };
      }
    }
    
    return null;
  }
  
  /**
   * Oblicz konfigurację dla rurek
   */
  private calculateTubeConfiguration(
    input: ShelfSystemInput,
    totalLoad: number,
    supportCount: number
  ): SupportConfiguration | null {
    // Standardowe rurki PMMA (średnica zewn. x wewn.)
    const standardTubes = [
      { outer: 10, inner: 6 },
      { outer: 12, inner: 8 },
      { outer: 15, inner: 11 },
      { outer: 20, inner: 16 },
      { outer: 25, inner: 19 },
      { outer: 30, inner: 24 },
      { outer: 40, inner: 34 },
      { outer: 50, inner: 44 }
    ];
    
    for (const tube of standardTubes) {
      if (input.maxSupportDiameter && tube.outer > input.maxSupportDiameter) {
        continue;
      }
      
      // Oblicz pole przekroju
      const area = Math.PI * ((tube.outer / 2) ** 2 - (tube.inner / 2) ** 2);
      const loadPerSupport = totalLoad / supportCount;
      const stress = (loadPerSupport * 9.81) / area;
      
      // Moment bezwładności dla rurki
      const momentOfInertia = Math.PI * (tube.outer ** 4 - tube.inner ** 4) / 64;
      const bucklingLoad = this.calculateBucklingLoad(
        momentOfInertia,
        input.supportHeight,
        'pinned-pinned'
      );
      const bucklingFactor = bucklingLoad / (loadPerSupport * 9.81);
      
      // Sprawdź bezpieczeństwo
      const allowableStress = this.material.compressiveStrength / input.safetyFactor;
      const isSafe = stress < allowableStress && bucklingFactor > (input.bucklingFactor || 2);
      
      if (isSafe) {
        const positions = this.calculateSupportPositions(input, supportCount);
        const maxDeflection = this.estimateMaxDeflection(input, positions);
        
        // Waga i koszt
        const volume = area * input.supportHeight * supportCount / 1000000;
        const weight = volume * this.material.density;
        const cost = weight * (this.material.pricePerKg || 30) * 1.2; // +20% za rurkę
        
        return {
          type: 'tube',
          diameter: tube.outer,
          innerDiameter: tube.inner,
          wallThickness: (tube.outer - tube.inner) / 2,
          count: supportCount,
          positions,
          material: 'PMMA',
          maxStressInSupport: stress,
          maxBuckling: bucklingFactor,
          maxShelfDeflection: maxDeflection,
          totalWeight: weight,
          totalCost: cost,
          isSafe: true,
          safetyMargin: ((1 - stress / allowableStress) * 100),
          criticalMode: stress / allowableStress > 0.8 ? 'stress' : 
                       bucklingFactor < 3 ? 'buckling' : 
                       maxDeflection > (input.maxShelfDeflection || input.shelfDepth / 200) ? 'deflection' : 
                       'none',
          recommendations: this.generateSupportRecommendations(tube.outer, supportCount, stress, bucklingFactor)
        };
      }
    }
    
    return null;
  }
  
  /**
   * Oblicz obciążenie krytyczne (wyboczenie)
   */
  private calculateBucklingLoad(
    momentOfInertia: number,
    length: number,
    endConditions: string
  ): number {
    const E = this.material.modulusOfElasticity || 3300; // MPa dla PMMA
    
    // Współczynnik długości efektywnej
    const K = endConditions === 'fixed-fixed' ? 0.5 :
              endConditions === 'fixed-pinned' ? 0.7 :
              endConditions === 'pinned-pinned' ? 1.0 :
              endConditions === 'fixed-free' ? 2.0 : 1.0;
    
    const effectiveLength = K * length;
    const criticalLoad = (Math.PI ** 2 * E * momentOfInertia) / (effectiveLength ** 2);
    
    return criticalLoad;
  }
  
  /**
   * Rozmieść podpory optymalnie
   */
  private calculateSupportPositions(
    input: ShelfSystemInput,
    count: number
  ): { x: number; y: number }[] {
    const positions: { x: number; y: number }[] = [];
    
    // Narożniki zawsze pierwsze
    if (input.cornerSupportsRequired !== false && count >= 4) {
      positions.push(
        { x: 50, y: 50 },                                        // lewy górny
        { x: input.shelfWidth - 50, y: 50 },                    // prawy górny
        { x: 50, y: input.shelfDepth - 50 },                    // lewy dolny
        { x: input.shelfWidth - 50, y: input.shelfDepth - 50 }  // prawy dolny
      );
    }
    
    // Dodatkowe podpory
    const remainingSupports = count - positions.length;
    
    if (remainingSupports > 0) {
      // Rozmieść równomiernie w siatce
      if (remainingSupports <= 2) {
        // Dodaj na środku długich boków
        positions.push(
          { x: input.shelfWidth / 2, y: 50 },
          { x: input.shelfWidth / 2, y: input.shelfDepth - 50 }
        );
      } else if (remainingSupports <= 4) {
        // Dodaj na środku wszystkich boków
        positions.push(
          { x: input.shelfWidth / 2, y: 50 },
          { x: input.shelfWidth / 2, y: input.shelfDepth - 50 },
          { x: 50, y: input.shelfDepth / 2 },
          { x: input.shelfWidth - 50, y: input.shelfDepth / 2 }
        );
      } else {
        // Siatka równomierna
        const cols = Math.ceil(Math.sqrt(count));
        const rows = Math.ceil(count / cols);
        
        for (let i = 0; i < rows; i++) {
          for (let j = 0; j < cols && positions.length < count; j++) {
            const x = 50 + (input.shelfWidth - 100) * j / (cols - 1);
            const y = 50 + (input.shelfDepth - 100) * i / (rows - 1);
            
            // Sprawdź czy nie duplikuje istniejącej pozycji
            const exists = positions.some(p => 
              Math.abs(p.x - x) < 10 && Math.abs(p.y - y) < 10
            );
            
            if (!exists) {
              positions.push({ x, y });
            }
          }
        }
      }
    }
    
    return positions.slice(0, count);
  }
  
  /**
   * Oszacuj maksymalne ugięcie półki
   */
  private estimateMaxDeflection(
    input: ShelfSystemInput,
    supports: { x: number; y: number }[]
  ): number {
    // Uproszczone oszacowanie - znajdź największą odległość od podpory
    let maxDistance = 0;
    
    // Sprawdź środek półki
    const centerX = input.shelfWidth / 2;
    const centerY = input.shelfDepth / 2;
    
    let minDistToSupport = Infinity;
    supports.forEach(support => {
      const dist = Math.sqrt(
        (centerX - support.x) ** 2 + 
        (centerY - support.y) ** 2
      );
      minDistToSupport = Math.min(minDistToSupport, dist);
    });
    
    maxDistance = minDistToSupport;
    
    // Wzór na ugięcie płyty
    const E = 3300; // MPa dla PMMA
    const t = input.shelfThickness;
    const q = input.uniformLoad / 1000000; // N/mm²
    const a = maxDistance * 2; // rozpiętość
    
    // Uproszczony wzór dla płyty kwadratowej
    const deflection = (0.0443 * q * a ** 4) / (E * t ** 3);
    
    return deflection;
  }
  
  /**
   * Analiza ugięcia półki
   */
  private analyzeShelfDeflection(
    input: ShelfSystemInput,
    config: SupportConfiguration
  ): any {
    const totalLoad = this.calculateTotalLoad(input);
    const loadPerSupport = config.positions.map(() => totalLoad / config.count);
    
    return {
      totalLoad,
      loadPerSupport,
      maxDeflection: config.maxShelfDeflection,
      deflectionMap: [], // TODO: implementacja mapy ugięć
      stressMap: []     // TODO: implementacja mapy naprężeń
    };
  }
  
  /**
   * Generuj rekomendacje dla podpór
   */
  private generateSupportRecommendations(
    diameter: number,
    count: number,
    stress: number,
    buckling: number
  ): string[] {
    const recommendations: string[] = [];
    
    if (diameter < 15) {
      recommendations.push('Rozważ większą średnicę dla lepszej stabilności');
    }
    
    if (count > 9) {
      recommendations.push('Duża liczba podpór - rozważ grubsze słupki i mniejszą ilość');
    }
    
    if (buckling < 4) {
      recommendations.push('Niski współczynnik wyboczenia - rozważ krótsze słupki lub większą średnicę');
    }
    
    if (stress > 20) {
      recommendations.push('Wysokie naprężenia - zwiększ liczbę podpór lub średnicę');
    }
    
    return recommendations;
  }
  
  /**
   * Generuj ogólne rekomendacje
   */
  private generateRecommendations(
    input: ShelfSystemInput,
    configs: SupportConfiguration[],
    analysis: any
  ): string[] {
    const recommendations: string[] = [];
    
    if (configs.length === 0) {
      recommendations.push('❌ Nie znaleziono bezpiecznej konfiguracji - zmniejsz obciążenie lub zwiększ dopuszczalne wymiary');
    } else {
      const best = configs[0];
      
      if (best.type === 'tube') {
        recommendations.push('✅ Rurki zapewniają lepszą sztywność przy mniejszej wadze');
      } else {
        recommendations.push('✅ Pręty są tańsze i łatwiejsze w montażu');
      }
      
      if (best.count <= 4) {
        recommendations.push('💡 Minimalna liczba podpór - ekonomiczne rozwiązanie');
      } else if (best.count >= 9) {
        recommendations.push('⚠️ Duża liczba podpór - rozważ wzmocnienie półki');
      }
      
      if (best.maxShelfDeflection > input.shelfDepth / 300) {
        recommendations.push('⚠️ Znaczne ugięcie półki - rozważ grubszą półkę lub więcej podpór');
      }
    }
    
    return recommendations;
  }
  
  /**
   * Generuj instrukcję montażu
   */
  private generateInstallationGuide(
    input: ShelfSystemInput,
    config?: SupportConfiguration
  ): string[] {
    if (!config) return [];
    
    const guide: string[] = [];
    
    guide.push('1. Przygotowanie:');
    guide.push(`   - Przytnij ${config.count} słupków PMMA ${config.type === 'tube' ? 'rurka' : 'pręt'} ø${config.diameter}mm na długość ${input.supportHeight}mm`);
    guide.push('   - Wygładź końce słupków papierem ściernym');
    
    guide.push('2. Oznaczenie pozycji:');
    config.positions.forEach((pos, i) => {
      guide.push(`   - Słupek ${i + 1}: ${pos.x}mm od lewej, ${pos.y}mm od góry`);
    });
    
    guide.push('3. Montaż:');
    guide.push('   - Użyj kleju do PMMA lub uchwytów mechanicznych');
    guide.push('   - Zachowaj pionowość słupków (użyj poziomicy)');
    guide.push('   - Odczekaj 24h przed pełnym obciążeniem (klej)');
    
    guide.push('4. Kontrola:');
    guide.push('   - Sprawdź stabilność konstrukcji');
    guide.push('   - Zmierz ugięcie półki pod obciążeniem');
    guide.push(`   - Max dopuszczalne ugięcie: ${(input.maxShelfDeflection || input.shelfDepth / 200).toFixed(1)}mm`);
    
    return guide;
  }
  
  /**
   * Domyślna konfiguracja gdy nie znaleziono rozwiązania
   */
  private getDefaultConfiguration(input: ShelfSystemInput): SupportConfiguration {
    return {
      type: 'rod',
      diameter: 20,
      count: 4,
      positions: [
        { x: 50, y: 50 },
        { x: input.shelfWidth - 50, y: 50 },
        { x: 50, y: input.shelfDepth - 50 },
        { x: input.shelfWidth - 50, y: input.shelfDepth - 50 }
      ],
      material: 'PMMA',
      maxStressInSupport: 0,
      maxBuckling: 0,
      maxShelfDeflection: 0,
      totalWeight: 0,
      totalCost: 0,
      isSafe: false,
      safetyMargin: 0,
      criticalMode: 'none',
      recommendations: ['Nie znaleziono optymalnej konfiguracji'],
      warnings: ['Zwiększ dopuszczalne parametry lub zmniejsz obciążenie']
    };
  }
}
